import { hubRequest, hubBody } from "./client";

/** Project-scoped authority is supplied by the server, never by model/tool arguments. */
export const AGENT_TOOLS = [
  "project.read", "progress.write", "issues.flag", "owners.remind", "decisions.submit",
] as const;
export type AgentToolName = (typeof AGENT_TOOLS)[number];
export type AgentStatus =
  | "active"
  | "waiting"
  | "paused"
  | "needs_decision"
  | "completed"
  | "error"
  | "budget_exhausted";
export type CompletionCriterion = {
  id: string;
  kind:
    "task_done" | "approval_approved" | "progress_written" | "artifact_exists" | "human_confirmed";
  description: string;
  entityId?: string;
};
export type AgentBudget = {
  maxStepsPerRun: number;
  maxWritesPerRun: number;
  maxRunsPerDay: number;
  maxTokensPerRun: number;
  minWakeMinutes: number;
  maxCostUsdPerRun?: number;
};
export const DEFAULT_AGENT_BUDGET: AgentBudget = {
  maxStepsPerRun: 8,
  maxWritesPerRun: 4,
  maxRunsPerDay: 24,
  maxTokensPerRun: 32000,
  minWakeMinutes: 30,
  maxCostUsdPerRun: 1,
};
export type Responsibility = {
  id: string;
  tenantId: string;
  customerId: string | null;
  projectId: string;
  ownerId: string;
  title: string;
  goal: string;
  status: AgentStatus;
  channelId: string | null;
  allowedTools: AgentToolName[];
  completionCriteria: CompletionCriterion[];
  budget: AgentBudget;
  nextWakeAt: number | null;
  lastRunAt: number | null;
  lastSummary: string;
  revision: number;
  createdAt: number;
  updatedAt: number;
};
export type AgentSource = {
  id: string;
  responsibilityId: string;
  kind: "message" | "minutes" | "task" | "approval" | "manual" | "decision";
  externalId: string;
  title: string;
  content: string;
  url: string;
  observedAt: number;
};
export type AgentMemory = {
  id: string;
  responsibilityId: string;
  kind: "fact" | "commitment" | "dependency" | "open_question";
  content: string;
  sourceIds: string[];
  updatedAt: number;
};
export type AgentEvidence = {
  criterionId: string;
  satisfied: boolean;
  reason: string;
  sourceUrl?: string;
  entityId?: string;
};
export type AgentDecision = {
  id: string;
  responsibilityId: string;
  question: string;
  version?: number;
  optionIds?: string[];
  options: string[];
  reason: string;
  status: "open" | "resolved";
  answer: string | null;
  resolvedBy: string | null;
  createdAt: number;
  resolvedAt: number | null;
};
export type AgentRun = {
  engine?: string;
  sessionId?: string;
  costUsd?: number;
  usageComplete?: boolean;
  sdkVersion?: string;
  id: string;
  responsibilityId: string;
  trigger: string;
  status:
    | "queued"
    | "running"
    | "waiting"
    | "completed"
    | "failed"
    | "needs_review"
    | "stopped";
  startedAt: number;
  finishedAt: number | null;
  steps: number;
  writes: number;
  tokens: number;
  summary: string;
  error: string | null;
};
export type AgentStep = {
  id: string;
  runId: string;
  responsibilityId: string;
  sequence: number;
  kind:
    | "observation"
    | "plan"
    | "tool"
    | "wait"
    | "decision"
    | "completion"
    | "error";
  tool: string | null;
  reason: string;
  input: unknown;
  output: unknown;
  status: "pending" | "sending" | "applied" | "failed" | "needs_review";
  actionId: string | null;
  createdAt: number;
};
export type AgentArtifact = {
  id: string;
  responsibilityId: string;
  title: string;
  content: string;
  sourceIds: string[];
  marker: string;
  createdAt: number;
  updatedAt: number;
};
export type CreateResponsibility = {
  projectId: string;
  title: string;
  goal: string;
  channelId?: string | null;
  allowedTools?: AgentToolName[];
  completionCriteria: CompletionCriterion[];
  budget?: Partial<AgentBudget>;
  idempotencyKey?: string;
};
export type AgentSession = { sessionId: string; revision: number; engine: string; sdkVersion: string | null; status: string };
export type ResponsibilityDetail = {
  engine?: string;
  session?: AgentSession | null;
  responsibility: Responsibility;
  sources: AgentSource[];
  memory: AgentMemory[];
  runs: AgentRun[];
  steps: AgentStep[];
  stepsTotal?: number;
  stepsNextOffset?: number;
  decisions: AgentDecision[];
  artifacts: AgentArtifact[];
  evidence: AgentEvidence[];
  canManage: boolean;
};
export type SourceInput = Omit<
  AgentSource,
  "id" | "responsibilityId" | "observedAt"
>;
export type ResponsibilityPatch = Partial<
  Pick<
    Responsibility,
    "title" | "goal" | "channelId" | "allowedTools" | "completionCriteria"
  >
> & { budget?: Partial<AgentBudget>; expectedRevision: number };
const path = (id: string) => `/project-agents/${encodeURIComponent(id)}`;
export const listProjectAgents = (projectId?: string) =>
  hubRequest<Responsibility[]>("/project-agents", {}, { projectId });
type UnifiedDecision = {
  id: string; responsibilityId: string; question: string;
  options: { id: string; label: string }[]; evidence: string; recommendation: string;
  status: "open" | "resolved"; version: number; createdAt: number;
  result?: { conclusion?: string; optionId?: string; deciderId?: string; resolvedAt?: number } | null;
};
export const listAgentDecisions = (id: string) =>
  hubRequest<{ items: UnifiedDecision[] }>("/action-decisions", {}, { responsibilityId: id });
export const getProjectAgent = async (id: string, stepsOffset = 0) => {
  const [detail, decisions] = await Promise.all([
    hubRequest<ResponsibilityDetail>(path(id), {}, { stepsOffset, stepsLimit: 40 }),
    listAgentDecisions(id),
  ]);
  const legacyResolved = detail.decisions.filter(d => d.status === "resolved" && !decisions.items.some(u => u.id === d.id));
  return { ...detail, decisions: [...legacyResolved, ...decisions.items.filter(d => d.responsibilityId === id && (d.status === "open" || d.status === "resolved")).map(d => ({
    id: d.id, responsibilityId: id, question: d.question, version: d.version,
    optionIds: d.options.map(o => o.id), options: d.options.map(o => o.label),
    reason: [d.recommendation, d.evidence].filter(Boolean).join("\n"), status: d.status,
    answer: d.result?.conclusion || d.options.find(o => o.id === d.result?.optionId)?.label || null,
    resolvedBy: d.result?.deciderId ?? null, createdAt: d.createdAt, resolvedAt: d.result?.resolvedAt ?? null,
  }))] };
};
export const createProjectAgent = (input: CreateResponsibility) =>
  hubRequest<Responsibility>("/project-agents", hubBody(input));
export const updateProjectAgent = (id: string, patch: ResponsibilityPatch) =>
  hubRequest<Responsibility>(path(id), hubBody(patch, "PATCH"));
export const changeProjectAgent = (
  id: string,
  action: "pause" | "resume" | "run",
) => hubRequest<{ queued?: boolean }>(`${path(id)}/${action}`, hubBody({}));
export const addAgentSource = (id: string, source: SourceInput) =>
  hubRequest<AgentSource>(`${path(id)}/sources`, hubBody(source));
export const resolveAgentDecision = (
  _id: string, decisionId: string, answer: string, version: number, optionId: string,
) => hubRequest<AgentDecision>(
  `/action-decisions/${encodeURIComponent(decisionId)}/resolve`,
  hubBody({ version, optionId, conclusion: answer }),
);
export const confirmAgentCriterion = (
  id: string,
  criterionId: string,
  evidence: string,
) =>
  hubRequest<Responsibility>(
    `${path(id)}/criteria/${encodeURIComponent(criterionId)}/confirm`,
    hubBody({ evidence }),
  );
export type AgentChannel = {
  chatId: string;
  projectId: string;
  verifiedAt: number;
};
export const listAgentChannels = (projectId?: string) =>
  hubRequest<AgentChannel[]>("/project-agents/channels", {}, { projectId });
export const bindAgentChannel = (projectId: string, chatId: string) =>
  hubRequest<AgentChannel>(
    "/project-agents/channels",
    hubBody({ projectId, chatId }),
  );
export const unbindAgentChannel = (chatId: string) =>
  hubRequest<{ chatId: string; projectId: string; affectedResponsibilities: number }>(
    `/project-agents/channels/${encodeURIComponent(chatId)}`,
    { method: "DELETE" },
  );
export type AgentDeliveryReview = {
  id: string;
  kind: "command" | "notification";
  title: string;
  channelId: string;
  createdAt: number;
  reason: string;
};
export const listAgentDeliveryReviews = (id: string) =>
  hubRequest<AgentDeliveryReview[]>(`${path(id)}/delivery-reviews`);
export const verifyAgentDelivery = (
  id: string,
  review: Pick<AgentDeliveryReview, "id" | "kind">,
  messageId: string,
) =>
  hubRequest<{ verified: boolean; messageId: string }>(
    `${path(id)}/delivery-reviews/${review.kind}/${encodeURIComponent(review.id)}/verify`,
    hubBody({ messageId }),
  );
export type AgentCustomerReview = {
  id: string;
  sourceActionId: string;
  responsibilityId: string;
  projectId: string;
  kind: "task_content" | "task_comment" | "project_progress";
  entityId: string;
  status: "staged" | "ready" | "publishing" | "published" | "rejected" | "needs_review";
  proposal: { title?: string; description?: string; content?: string; health?: string };
  publicValue: { title?: string; description?: string };
  createdAt: number;
  reviewedAt: number | null;
};
export const listAgentCustomerReviews = (id: string) =>
  hubRequest<AgentCustomerReview[]>(`${path(id)}/customer-reviews`);
export const decideAgentCustomerReview = (
  id: string,
  reviewId: string,
  decision: "publish" | "reject" | "reconcile",
  edited: Record<string, string>,
) => hubRequest<AgentCustomerReview>(
  `${path(id)}/customer-reviews/${encodeURIComponent(reviewId)}/decide`,
  hubBody({ decision, edited }),
);
